import React from "react";
import PropTypes from "prop-types";
import { ImageDiscoveryCanvasRedux } from "@learningblocks/interactives/interactive-infographic-components";
import { ContentDropdown } from "../../ContentDropdown";
import { QuestionWrapper } from "../Components/QuestionWrapper";
import { Question } from "../Components/Question";
import { MainTitle } from "../Components/MainTitle";
import { QuestionImage } from "../Components/QuestionImage";
import { Utilities } from "@learningblocks/exp-constants";
import { Option } from "./Option";
import { Footer } from "../Components/Footer";
import { SubmitButton } from "../Components/SubmitButton";
import { Button } from "@labxchange/buttons";
import { TotalAttempts } from "../Components/TotalAttempts";
import { useIntl } from "react-intl";
import { NotebookImage } from "@learningblocks/simulations/shared-components";
import { RichText } from "@learningblocks/shared-components/lib/RichText";
import {
  QuestionFeedbackComponent,
  FEEDBACK_SUCCESS,
  FEEDBACK_FAIL,
} from "../Components/QuestionFeedbackComponent";

import "./style.scss";

const { QUESTION_CONTENTS } = Utilities;

/**
 * ImageSelectionQuestion [IMAGE_SELECTION_QUESTION]:
 *  - You will have n choices to select from
 *  - The question will have a single correct Answer on single place.
 */
export function ImageSelectionQuestion(props) {
  const intl = useIntl();

  const checkCSCQHasUnsubmittedOptions = (optionList) => {
    const unsubmittedOptionCount = optionList.reduce((count, option) => {
      if (option.disabled === false) {
        count += 1;
      }
      return count;
    }, 0);
    return unsubmittedOptionCount !== 0;
  };

  const resetButtonText = {
    id: "Reset options",
    defaultMessage: "Reset options",
    description: "",
  };

  const setSubmitDisabled = () => {
    if (props.totalAttempts == 0 || props.complete || props.attempted)
      return true;
    else {
      return checkCSCQHasUnsubmittedOptions(props.options);
    }
  };

  const setResetDisabled = () => {
    return props.totalAttempts == 0 || props.isComplete;
  };

  const selectOption = (params) =>
    props.selectOption({
      ...params,
      componentID: props.id,
      selectedHotspot: props.selectedHotspot,
    });

  const onReset = () => props.resetAnswer(props.id, props.type);

  const onSubmit = () => {
    const correctness = props.selectedOptions.map((selected) => {
      const selectedKey = Object.keys(selected)[0];
      const selectedValue = selected[selectedKey];

      const matchingAnswer = props.answerId.find(
        (answer) => Object.keys(answer)[0] === selectedKey
      );

      const isCorrect =
        matchingAnswer && matchingAnswer[selectedKey] === selectedValue;

      return { [selectedKey]: isCorrect };
    });
    props.submitAnswer(props.id, correctness);
  };

  const renderQuestion = (key, question) => (
    <Question
      tabIndex={"0"}
      key={key}
      text={question}
      className="isq__question"
    />
  );

  const renderText = (key, text) => (
    <Question tabIndex={"0"} key={key} text={text} className="isq__content" />
  );

  const renderImage = (key, image) => (
    <QuestionImage
      tabIndex={"0"}
      key={key}
      altText={props.altText}
      image={image}
      className="isq__image"
      preview={props.preview}
    />
  );

  const figcaption = (key, content) => {
    return (
      <div style={{ marginBottom: "16px" }}>
        <NotebookImage
          key={key}
          {...{ image: content }}
          getGlossaryItem={props.getGlossaryItem}
          openGlossary={props.openGlossary}
        />
      </div>
    );
  };

  const renderOptions = (key, options) => {
    return (
      <div className="isq__options_wrapper">
        <p>Select the correct image for the boxes.</p>
        <div class="isq__options_row">
          {options.map((option, id) => {
            const index = `${key}_${id}`;
            return (
              <Option
                key={index}
                {...option}
                index={id}
                tabIndex={"0"}
                selectOption={selectOption}
              />
            );
          })}
        </div>
      </div>
    );
  };

  const hintDetailsSection = (key, hintDetails) => {
    return <ContentDropdown key={key} contentDetails={hintDetails} />;
  };

  const imageDiscoveryCanvas = (key, gridData) => {
    return (
      <>
        <ImageDiscoveryCanvasRedux
          key={key}
          sceneData={gridData.data}
          preview={false}
          standalone={false}
          isDiabledBlurOnSmall={true}
          selectedHotspot={props.selectedHotspot}
          componentID={props.id}
        />
        <figcaption className="isq__figcaption">
          <RichText {...gridData.caption} />
        </figcaption>
      </>
    );
  };

  const renderQuestionFeedback = (incorrectHotspots, attempted) => {
    if (incorrectHotspots.length > 0 && attempted) {
      return incorrectHotspots.map(([id, data]) => {
        const feedbackProps = {
          className: "",
          message: data.props.feedback,
          type: FEEDBACK_FAIL,
        };
        return <QuestionFeedbackComponent key={id} {...feedbackProps} />;
      });
    } else if (attempted) {
      const feedbackProps = {
        className: "",
        message: "Correct!",
        type: FEEDBACK_SUCCESS,
      };
      return <QuestionFeedbackComponent {...feedbackProps} />;
    }
  };

  const renderByType = (type, key, content) => {
    switch (type) {
      case QUESTION_CONTENTS.QUESTION:
        return renderQuestion(key, content);
      case QUESTION_CONTENTS.TEXT:
        return renderText(key, content);
      case QUESTION_CONTENTS.IMAGE:
        return renderImage(key, content);
      case QUESTION_CONTENTS.FIGCAPTION:
        return figcaption(key, content);
      case QUESTION_CONTENTS.OPTIONS:
      case QUESTION_CONTENTS.TYPED_OPTIONS:
        return renderOptions(key, content);
      case QUESTION_CONTENTS.HINTS:
        return hintDetailsSection(key, props.hintDetails);
      case QUESTION_CONTENTS.CONTENT_LIST:
        return content.map((listItem, subKey) =>
          renderByType(listItem.type, `${key}_${subKey}`, listItem.content)
        );
      case QUESTION_CONTENTS.GRID:
        return imageDiscoveryCanvas(key, content);
      default:
        return null;
    }
  };

  const wrapperClass = ` isq ${props.className}`;

  return (
    <QuestionWrapper className={wrapperClass}>
      <MainTitle
        title={intl.formatMessage({
          id: "IMAGE SELECTION QUESTION",
          defaultMessage: "IMAGE SELECTION QUESTION",
          description: "",
        })}
        className="isq__mainTitle"
      />
      {props.content.map((type, key) => renderByType(type, key, props[type]))}
      {renderQuestionFeedback(
        Object.entries(props.grid.data.hotspots).filter(
          ([, data]) => data.isCorrect === false
        ),
        props.attempted
      )}
      <Footer className="isq__footer">
        {props.totalAttempts > 0 && (
          <TotalAttempts
            totalAttempts={props.totalAttempts}
            className="isq__footer__totalAttempts"
          />
        )}
        <div>
          <Button
            className="secondary-button"
            onClick={onReset}
            titleDescriptor={resetButtonText}
            iconName={null}
            disabled={setResetDisabled()}
          />
          <SubmitButton
            label={intl.formatMessage({
              id: "Submit answer",
              defaultMessage: "Submit answer",
              description: "",
            })}
            onSubmit={onSubmit}
            disabled={setSubmitDisabled()}
          />
        </div>
      </Footer>
    </QuestionWrapper>
  );
}

ImageSelectionQuestion.propTypes = {
  /**
   * The prop for the ID of correct answer option along with hotspotId
   */
  answerId: PropTypes.arrayOf(PropTypes.object).isRequired,
  /**
   * The prop for the ID of selected option along with hotspotId
   */
  selectedOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  /**
   * The custom classname you wanna inject on the root of the component
   */
  className: PropTypes.string,
  /**
   * The prop to set if the component is complete
   */
  complete: PropTypes.bool.isRequired,
  /**
   * List of static contents of type question, text, and image you want to display
   */
  contentList: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Type of content Item
       */
      type: PropTypes.oneOf(["question", "image", "text"]),
      /**
       * Content Item assicated with the type
       */
      content: PropTypes.string,
    })
  ),
  /**
   * The prop to specify all contents in the question
   */
  content: PropTypes.arrayOf(PropTypes.string).isRequired,
  /**
   * The prop to specify all incorrect options the question
   */
  inCorrectOptions: PropTypes.arrayOf(PropTypes.number),
  /**
   * The prop that hold the unique id for the component
   */
  id: PropTypes.number,
  /**
   * The prop that hold image data
   */
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /**
   * The prop that hold the array of answer options.
   */
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  /**
   * The prop that holds the text for the question
   */
  question: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    .isRequired,
  /**
   * The prop that holds the total attempts,
   */
  totalAttempts: PropTypes.number.isRequired,
  /**
   * The prop that holds the Component type.
   */
  type: PropTypes.oneOf(["IMAGE_SELECTION_QUESTION"]),
  /**
   * The callback prop executed when submit is clicked
   */
  submitAnswer: PropTypes.func.isRequired,
  /**
   * The callback to be executed when an option is selected
   */
  selectOption: PropTypes.func.isRequired,
  /**
   * The content that is shown in the hint sectoin
   */
  hintDetails: PropTypes.shape({
    /**
     * The label that is added to the hint section. Usually Hint
     */
    label: PropTypes.string.isRequired,
    /**
     * Description provided for the hint section
     */
    description: PropTypes.string.isRequired,
    /**
     * To show the question title
     */
    title: PropTypes.string,
    /**
     * className to be set for ISQColor
     */
    className: PropTypes.string,
  }),
  /**
   * Submit Button Label
   */
  submitLabel: PropTypes.string,
  /**
   * show attempted
   */
  attempted: PropTypes.bool,
  /**
   * The prop that hold the unique selected hotspotId
   */
  selectedHotspot: PropTypes.number,
};

ImageSelectionQuestion.defaultProps = {
  className: "",
  answerId: [],
  selectedOptions: [],
  complete: false,
  content: [],
  image: "",
  options: [],
  question: "",
  totalAttempts: 0,
  caption: "",
  preview: false,
  submitLabel: "Submit answer",
  attempted: false,
  selectedHotspot: null,
};
